import React from 'react';
import WordElement from './WordElement';
import { WordStatus, TextDisplayMode, IReadingWord } from '@shared/types';
import { useStoreByName } from '@web/hooks/use-stores.hooks';
import { observer } from 'mobx-react-lite';

// ---- טיפוסים ----
interface LineElementProps {
  content: string;
  parsedWords: string[]; // המילים המפוצלות מראש
  startWordIndex: number; // האינדקס ההתחלתי של השורה
  mode: TextDisplayMode;
  fontClassName: string;
  wordStatuses?: any[]; // עבור תצוגת תוצאות סטטיות (במצב result בלבד)
  showArrowIndicator?: boolean;
  className?: string;
  readingWords?: IReadingWord[]; // המילים מה-ReadingSession (במצבי reading/byHeart)
}

/**
 * קובע את הסטטוס של מילה בהתבסס על הנתונים מה-ReadingSession או מ-wordStatuses
 */
const determineWordStatus = (
  wordIndex: number,
  readingWords?: IReadingWord[],
  wordStatuses?: any[]
): WordStatus => {
  // אם יש סטטוסים מוגדרים מראש (במצב result) - השתמש בהם
  if (wordStatuses?.[wordIndex]?.wordStatus) {
    return wordStatuses[wordIndex].wordStatus;
  }

  // אם יש readingWords (במצבי reading/byHeart) - השתמש במידע מה-ReadingSession
  if (readingWords && readingWords[wordIndex]) {
    return readingWords[wordIndex].status;
  }

  // אחרת - ממתין (ברירת מחדל)
  return WordStatus.Pending;
};

/**
 * קובע אם המילה היא המילה הנוכחית
 */
const determineIsCurrentWord = (wordIndex: number, readingWords?: IReadingWord[], wordStatuses?: any[]): boolean => {
  // אם יש סטטוסים מוגדרים מראש (במצב result), לא משתמשים במילה נוכחית
  if (wordStatuses) {
    return false;
  }
  
  // אם יש readingWords - השתמש במידע מה-ReadingSession
  if (readingWords && readingWords[wordIndex]) {
    return readingWords[wordIndex].isCurrentWord;
  }
  
  return false;
};

// ---- הרכיב הראשי ----

/**
 * רכיב שמייצג שורה אחת של טקסט, מפצל למילים ויוצר WordElement לכל מילה
 */
const LineElement: React.FC<LineElementProps> = observer(({
  content,
  parsedWords,
  startWordIndex,
  mode,
  fontClassName,
  wordStatuses,
  showArrowIndicator = true,
  className,
  readingWords,
}) => {
  // אם יש readingWords (מה-ReadingSession), השתמש בהם. אחרת, השתמש בסטור הישן
  const speechStore = !readingWords ? useStoreByName('speechRecognitionStore') as any : null;
  
  // נתוני דיבור (רק אם אין readingWords)
  const getErrorAttemptId = speechStore?.getErrorAttemptId;
  return (
    <p className={`line-element ${className || ''} ${fontClassName}`}>
      {parsedWords.map((word, index) => {
        const currentLineWordIndex = startWordIndex + index; // שמירת האינדקס הנוכחי לפני העדכון

        const status = determineWordStatus(
          currentLineWordIndex,
          readingWords,
          wordStatuses
        );

        const isCurrent = determineIsCurrentWord(currentLineWordIndex, readingWords, wordStatuses);

        // בחירת errorAttemptId בהתאם למקור הנתונים
        let errorAttemptId = undefined;
        if (readingWords && readingWords[currentLineWordIndex]) {
          errorAttemptId = readingWords[currentLineWordIndex].errorAttemptId;
        } else if (speechStore && typeof getErrorAttemptId !== 'undefined') {
          errorAttemptId = getErrorAttemptId;
        }

        const errorProps =
          isCurrent && status === WordStatus.Mistake && errorAttemptId !== undefined
            ? { errorAttemptId }
            : {};

        const element = (
          <WordElement
            key={`word-${currentLineWordIndex}-${index}`}
            word={word}
            status={status}
            isCurrentWord={isCurrent}
            mode={mode}
            data-word-index={currentLineWordIndex}
            showArrowIndicator={showArrowIndicator}
            {...errorProps}
          />
        );

        return (
          <React.Fragment key={`fragment-${index}`}>
            {element}
            {index < parsedWords.length - 1 && ' '}
          </React.Fragment>
        );
      })}
    </p>
  );
});

export default LineElement;
